"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";

import { cn } from "../../utils/cn.ts";
import { FontStyles } from "../../utils/font-styles.ts";
import { focusStylesOutline } from "../../utils/focus-styles.ts";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-solid.css";

// Keep the ring circular: focusStylesOutline rounds the focused element to rounded-sm
const radioFocusStyles = cn(focusStylesOutline, "focus-visible:rounded-full");

type State = "default" | "error";

interface BaseRadioGroupProps extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {
  state?: State;
  message?: string;
  label?: string;
  labelSize?: "default" | "large";
  showRequiredAsterisk?: boolean;
}

const RadioGroupContext = React.createContext<{ state?: State }>({ state: "default" });

const BaseRadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, BaseRadioGroupProps>(
  (
    { className, state = "default", message, label, labelSize = "default", showRequiredAsterisk = false, ...props },
    ref,
  ) => {
    const labelStyle = `text-slate font-semibold ${labelSize === "large" ? FontStyles["body-md"] : FontStyles["body-s"]} leading-snug md:leading-snug`;
    const baseMessageStyle = "font-normal " + FontStyles["body-s"];
    const messageStyle = cn(baseMessageStyle, state === "error" ? "text-brick" : "text-light-slate");

    return (
      <RadioGroupContext.Provider value={{ state }}>
        <div className="flex flex-col gap-3 md:gap-3.5">
          {label && (
            <span className={labelStyle}>
              {label}
              {showRequiredAsterisk && <span className="text-brick"> *</span>}
            </span>
          )}
          <RadioGroupPrimitive.Root className={cn("flex flex-col gap-3.5", className)} {...props} ref={ref} />
          {message && <span className={messageStyle}>{message}</span>}
        </div>
      </RadioGroupContext.Provider>
    );
  },
);
BaseRadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  const { state } = React.useContext(RadioGroupContext);
  const isError = state === "error";

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        // base styles
        "group aspect-square h-4.5 w-4.5 rounded-full shadow-none border-2 disabled:opacity-50 cursor-pointer",
        // default state
        isError
          ? cn(
              "border-brick hover:border-brick-600 data-[state=checked]:border-brick-200 data-[state=checked]:hover:border-brick-300",
              radioFocusStyles,
            )
          : cn(
              "border-foggy-firth hover:border-neutral-300 data-[state=checked]:border-loch-100 data-[state=checked]:hover:border-loch-200",
              radioFocusStyles,
            ),
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <i
          className={cn(
            "text-[8px] fa-sharp fa-solid fa-circle",
            isError ? "text-brick group-hover:text-brick-600" : "text-loch group-hover:text-north-sea-600",
          )}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { BaseRadioGroup, RadioGroupItem };
